import { useState, useEffect, useCallback } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, Autocomplete, CircularProgress } from "@mui/material";
import apiService from "../../service/apiService";
import { FaRegEdit, FaEye, FaTrash } from "react-icons/fa";
import { Pagination } from "react-bootstrap";
import '../../assets/styles/cropmanage.css';
import { debounce } from "lodash";

const BlockTable = () => {
  // Essential state variables only
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [newState, setNewState] = useState({ blockName: "", districtId: "" });
  const [statename, setStateName] = useState("");
  const [distname, setDistname] = useState("");
  const [blockname, setBlockName] = useState("");
  const [alldata, setAllData] = useState([]);
  const [totalItems, setTotalitems] = useState(0);
  const [totalpage, setTotalpage] = useState(0);
  const [deletedata, setDeleteData] = useState([]);
  const [openeditmodal, setOpenEditmodal] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const [dist, setDist] = useState(loginData?.districtId ? loginData.districtId : 0);
  const [blk, setBlk] = useState(loginData?.blockId ? loginData.blockId : 0);

  const fetchData = async () => {
    try {
      const response = await apiService.post("/blocks/get-all-block-index-data", {
        districtId: 0,
        blockId: 0,
        mipId: 0,
        villageId: 0,
        pageNumber: page,
        pageSize: pageSize,
        blockName: blockname,
        districtName: distname,
        stateName: statename,
      });

      const data = response.data.body.data;
      setAllData(data);

      const formattedData = data.map((item) => ({
        id: item.blockId,
        districtName: item.districtName,
        blockName: item.blockName,
        stateName: item.stateName,
        blockId: item.blockId,
      }));

      setTotalitems(response.data.body.recordsCount);
      setTotalpage(response.data.body.pageCount);
      setRows(formattedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAllData([]);
      setTotalitems(0);
      setTotalpage(0);
    }
  };

  const debouncedFetchData = useCallback(debounce(fetchData, 500), [
    blockname, distname, statename, page, pageSize
  ]);

  useEffect(() => {
    debouncedFetchData();
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  const fetchAllDist = async () => {
    try {
      const response = await apiService.get('/districts/get-all');
      const currentDist = loginData?.districtId ? loginData.districtId : 0;

      if (currentDist) {
        const defDist = response.data.find((dis) => dis.districtId === currentDist);
        setDistname(defDist ? defDist.districtName : "");
      }
    } catch (error) {
      setDistname("");
      console.error('Error fetching districts:', error);
    }
  };

  const fetchAllBlocks = async () => {
    try {
      const response = await apiService.get(`/master/get-block-by-district/${dist}`);
      const currentBlock = loginData?.blockId ? loginData.blockId : 0;

      if (currentBlock) {
        const defBlock = response.data.body.blocks.find((blk) => blk.blockId === currentBlock);
        setBlockName(defBlock ? defBlock.blockName : "");
      }
    } catch (error) {
      setBlockName("");
      console.error("Error fetching blocks:", error);
    }
  };

  useEffect(() => {
    if (dist > 0) {
      fetchAllBlocks();
    }
  }, [dist]);

  useEffect(() => {
    fetchAllDist();
  }, []);

  // Fetch districts for dropdown
  useEffect(() => {
    const fetchDistricts = async () => {
      setLoading(true);
      try {
        const response = await apiService.get('/districts/get-all');
        setDistricts(response.data);
      } catch (error) {
        console.error('Error fetching districts:', error);
      }
      setLoading(false);
    };

    fetchDistricts();
  }, []);

  const handleEdit = (data) => {
    setSelectedRow(data);
    setOpenEdit(true);
  };

  const handleView = (data) => {
    setSelectedRow(data);
    setOpen(true);
  };

  const handleDeleteClick = (data) => {
    setDeleteData(data);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setNewState({ blockName: "", districtId: "" });
    setInputValue('');
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedRow(null);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleConfEdit = () => {
    setOpenEditmodal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const { blockId } = selectedRow;
      const response = await apiService.put(`/blocks/update/${blockId}`, {
        blockName: selectedRow.blockName,
      });

      console.log("Update response:", response.data);
      fetchData();
    } catch (error) {
      console.error("Error updating data:", error);
    } finally {
      setOpenEdit(false);
      setOpenEditmodal(false);
    }
  };

  const handleSaveAdd = async () => {
    try {
      const response = await apiService.post("/blocks/create", {
        blockName: newState.blockName,
        districtId: newState.districtId,
      });
      console.log("Create response:", response.data);
      fetchData();
    } catch (error) {
      console.error("Error creating block:", error);
    } finally {
      handleCloseAdd();
    }
  };

  const handleDelete = async () => {
    try {
      const blockId = deletedata.blockId;
      await apiService.delete(`/blocks/delete/${blockId}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting data:", error);
    } finally {
      setOpenDelete(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const renderPaginationItems = () => {
    const paginationItems = [];
    const maxPagesToShow = 7;
    const startPages = 3;
    const endPages = 2;

    if (totalpage <= maxPagesToShow) {
      for (let pages = 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    } else {
      for (let pages = 1; pages <= startPages; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      if (page > startPages + 1) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      const middlePages = [];
      const startMiddlePage = Math.max(page - 1, startPages + 1);
      const endMiddlePage = Math.min(page + 1, totalpage - endPages);

      for (let pages = startMiddlePage; pages <= endMiddlePage; pages++) {
        middlePages.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      paginationItems.push(...middlePages);

      if (page < totalpage - endPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      for (let pages = totalpage - endPages + 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    }
    return paginationItems;
  };

  const startItem = (page - 1) * 10 + 1;
  const endItem = Math.min(page * 10, totalItems);

  // Filter districts based on user input
  const filteredDistricts = districts.filter(district =>
    district.districtName.toLowerCase().includes(inputValue.toLowerCase())
  );

  const selectedDistrict = districts.find(district => district.districtId === newState.districtId);

  return (
    <Box m="2px">
      <div style={{ maxHeight: "100vh", overflowY: "auto" }}>
        {/* Header - Same design as distdata */}
        <div style={{
          backgroundColor: "#ffffff",
          borderBottom: "3px solid #007bff",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}>
          <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center py-3 px-4">
              <div className="d-flex align-items-center">
                <div>
                  <h1 style={{
                    fontSize: "1.8rem",
                    fontWeight: "600",
                    color: "#2c3e50",
                    margin: "0"
                  }}>
                    Block Management
                  </h1>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="contained"
                  onClick={() => setOpenAdd(true)}
                  style={{
                    backgroundColor: "#28a745",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: "500",
                    textTransform: "none"
                  }}
                >
                  Add Block
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="mx-2" style={{ minHeight: "75vh", overflowY: "auto", overflowX: "auto", maxWidth: "100%", whiteSpace: "nowrap" }}>
          <table className="table table-hover mb-0" style={{
            minWidth: "800px",
            backgroundColor: "#fff"
          }}>
            <thead style={{
              backgroundColor: "#007bff",
              color: "white",
              position: "sticky",
              top: "0",
              zIndex: "10"
            }}>
              <tr>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "200px"
                }}>
                  Block Name
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={blockname}
                    onChange={(e) => {
                      if (blk === 0) {
                        setBlockName(e.target.value);
                        setPage(1);
                      }
                    }}
                    disabled={blk > 0}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "200px"
                }}>
                  District Name
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={distname}
                    onChange={(e) => {
                      if (dist === 0) {
                        setDistname(e.target.value);
                        setPage(1);
                      }
                    }}
                    disabled={dist > 0}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "200px"
                }}>
                  State Name
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={statename}
                    onChange={(e) => { setStateName(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  textAlign: "center",
                  minWidth: "150px"
                }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "13px" }}>
              {alldata.map((data, index) => (
                <tr key={index} style={{
                  backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#ffffff",
                  borderBottom: "1px solid #dee2e6"
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#e3f2fd";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#f8f9fa" : "#ffffff";
                  }}>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6",
                    fontWeight: "500"
                  }}>
                    {data.blockName}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.districtName}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.stateName}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    textAlign: "center"
                  }}>
                    <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleView(data)}
                        title="View"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleEdit(data)}
                        title="Edit"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaRegEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteClick(data)}
                        title="Delete"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className='d-flex justify-content-between pb-0'>
          <div className='p-2'>
            Showing {startItem} to {endItem} of {totalItems} items
          </div>
          <div className='p-2'>
            <Pagination className="mb-0">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalpage} />
              <Pagination.Last onClick={() => handlePageChange(totalpage)} disabled={page === totalpage} />
            </Pagination>
          </div>
        </div>

        {/* View Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>Block Management</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Block Name:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.blockName}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>District Name:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.districtName}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>State Name:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.stateName}</Typography>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary" variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Block</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <TextField
                autoFocus
                margin="dense"
                label="Block Name"
                type="text"
                fullWidth
                variant="outlined"
                value={selectedRow.blockName}
                onChange={(e) => setSelectedRow({ ...selectedRow, blockName: e.target.value })}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit} color="primary">Cancel</Button>
            <Button onClick={handleConfEdit} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Dialog */}
        <Dialog open={openAdd} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Block</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Block Name"
              fullWidth
              variant="outlined"
              value={newState.blockName}
              onChange={(e) => setNewState({ ...newState, blockName: e.target.value })}
            />
            <Box sx={{ marginTop: 2 }}>
              <Autocomplete
                value={selectedDistrict || null}
                onChange={(event, newValue) => {
                  setNewState({ ...newState, districtId: newValue ? newValue.districtId : '' });
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                options={filteredDistricts}
                getOptionLabel={(option) => option.districtName}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="District Name"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: loading ? <CircularProgress color="inherit" size={20} /> : null,
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.districtId}>
                    {option.districtName} ({option.stateName})
                  </li>
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAdd} color="primary">Cancel</Button>
            <Button onClick={handleSaveAdd} color="primary">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete}>
          <DialogTitle>Delete Block</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this block?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDelete} color="primary">Cancel</Button>
            <Button onClick={handleDelete} color="primary">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Confirmation Dialog */}
        <Dialog open={openeditmodal} onClose={() => setOpenEditmodal(false)}>
          <DialogTitle>Edit Block</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to edit this block?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditmodal(false)} color="primary">No</Button>
            <Button onClick={handleSaveEdit} color="primary">Yes</Button>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};

export default BlockTable;